/**
 * Constantes d’équilibrage NG+ partagées client / serveur.
 * Courbe ASC 1 → 30 : palier NG+ dès la 1ʳᵉ ascension, montée progressive.
 * Maîtrise de zone conservée à l’ascension — la difficulté passe par ces multiplicateurs.
 */

/** Aligné `shared/ascension_unlock.ts` (évite import circulaire). */
export const ASCENSION_UNLOCK_LEVEL = 175
export const ASCENSION_UNLOCK_BADGES = 90
export const ASCENSION_KILLS_PER_LEVEL = 165
export const ASCENSION_KILLS_PER_BADGE = 325
export const ASCENSION_KILLS_INCREASE_PER_ASC = 1_000
export const ASCENSION_KILLS_CAP = 50_000

/** Kills minimum au palier B (175 / 90 badges), indépendant du sur-farm. */
export const ASCENSION_UNLOCK_KILLS_GATE_FLOOR = Math.max(
  ASCENSION_UNLOCK_LEVEL * ASCENSION_KILLS_PER_LEVEL,
  ASCENSION_UNLOCK_BADGES * ASCENSION_KILLS_PER_BADGE,
)

/**
 * Plafond niveau d’ascension pour PV / pression / récompenses combat.
 * 30 → 60 (2026-06) : les sinks permanents (Puits/Transcendance/Pokédex) faisaient
 * trivialiser l’ascension au-delà de NG+30. N’impacte que les joueurs > NG+30
 * (`min(cap, niveau)` → courbe ≤ 30 inchangée). asc 60 : routes ×18,6 · pression ×0,53.
 */
export const ASCENSION_POWER_LEVEL_CAP = 60

/**
 * Routes / sauvages / boss de zone — croissance linéaire + plancher NG+ dès asc ≥ 1.
 * asc 0 → ×1,0 · asc 1 → ×1,51 · asc 30 → ×9,92
 */
export const ASCENSION_NG_ROUTE_HP_FLOOR = 0.22
/** Plancher PV routes au 1er palier NG+ (rampe asc 1→4). */
export const ASCENSION_NG_ROUTE_HP_FLOOR_EARLY = 0.1
/** Niveau d’ascension où le plancher routes atteint `ASCENSION_NG_ROUTE_HP_FLOOR`. */
export const ASCENSION_NG_ROUTE_HP_FLOOR_RAMP_AT = 4
export const ASCENSION_ROUTE_ENEMY_HP_STEP = 0.25

/**
 * Tour — plancher NG+ ramp asc 1→4 (rebuild post-reset jouable), plein dès asc 4.
 * asc 0 → ×1,0 · asc 1 → ×1,29 · asc 3 → ×1,97 · asc 4+ → courbe dure · asc 30 → ×3,95
 */
export const ASCENSION_NG_TOWER_HP_FLOOR = 1.05
/** Plancher tour au 1er palier NG+ (aligné routes) avant montée vers le plancher plein. */
export const ASCENSION_NG_TOWER_HP_FLOOR_EARLY = 0.22
/** Niveau d’ascension où le plancher tour atteint `ASCENSION_NG_TOWER_HP_FLOOR`. */
export const ASCENSION_NG_TOWER_HP_FLOOR_RAMP_AT = 4
export const BATTLE_TOWER_ASCENSION_HP_STEP = 0.065
/** Aligné `ASCENSION_POWER_LEVEL_CAP` (30 → 60, 2026-06). asc 60 tour : ×5,95. */
export const BATTLE_TOWER_ASCENSION_POWER_CAP = 60

/** Chaque rang « Mitigation » (routes + tour). */
export const ASCENSION_ENEMY_MITIGATION_STEP = 0.055

/** Malus DPS routes par niveau d’ascension effectif. asc 30 → ×0,69. */
export const ASCENSION_COMBAT_PRESSURE_PER_LEVEL = 0.015

/** Pression DPS en tour : fraction de la pression routes (asc 30 tour → ×0,77). */
export const ASCENSION_TOWER_COMBAT_PRESSURE_RATIO = 0.65

/** Or / XP boss & sauvages par niveau NG+ (asc 30 → ×7,0). */
export const ASCENSION_POWER_STEP = 0.2

export function roundAscensionMultiplier(value: number): number {
  return Math.round(value * 1000) / 1000
}

export function getAscensionEffectivePowerLevel(ascensionLevel: number): number {
  return Math.min(
    ASCENSION_POWER_LEVEL_CAP,
    Math.max(0, Math.floor(Number(ascensionLevel) || 0)),
  )
}

/** Quota kills pour ascensionner — ne dépend plus du niveau/badges actuels au-delà du palier B. */
export function getAscensionUnlockKills(ascensionLevel: number): number {
  const asc = Math.max(0, Math.floor(Number(ascensionLevel) || 0))
  return Math.min(
    ASCENSION_KILLS_CAP,
    ASCENSION_UNLOCK_KILLS_GATE_FLOOR + asc * ASCENSION_KILLS_INCREASE_PER_ASC,
  )
}

/** Plancher PV routes : doux asc 1, montée linéaire jusqu’au plancher plein (asc 4+). */
export function getRouteNgHpFloor(effectiveAscensionLevel: number): number {
  const effective = Math.max(0, Math.floor(Number(effectiveAscensionLevel) || 0))
  if (effective <= 0) return 0
  const rampEnd = Math.max(2, Math.floor(Number(ASCENSION_NG_ROUTE_HP_FLOOR_RAMP_AT) || 4))
  if (effective >= rampEnd) return ASCENSION_NG_ROUTE_HP_FLOOR
  const progress = (effective - 1) / (rampEnd - 1)
  return (
    ASCENSION_NG_ROUTE_HP_FLOOR_EARLY +
    progress * (ASCENSION_NG_ROUTE_HP_FLOOR - ASCENSION_NG_ROUTE_HP_FLOOR_EARLY)
  )
}

/** Mult. PV routes / sauvages / boss de zone (NG+). */
export function getRouteEnemyHpMult(
  ascensionLevel: number,
  enemyMitigationRanks: number = 0,
): number {
  const effective = getAscensionEffectivePowerLevel(ascensionLevel)
  const mitigation = Math.max(0, Math.floor(Number(enemyMitigationRanks) || 0))
  const floor = getRouteNgHpFloor(effective)
  return roundAscensionMultiplier(
    Math.max(
      0.93,
      1 +
        floor +
        effective * ASCENSION_ROUTE_ENEMY_HP_STEP -
        mitigation * ASCENSION_ENEMY_MITIGATION_STEP,
    ),
  )
}

/** Plancher PV tour : doux asc 1, montée linéaire jusqu’au plancher plein (asc 4+). */
export function getBattleTowerNgHpFloor(effectiveAscensionLevel: number): number {
  const effective = Math.max(0, Math.floor(Number(effectiveAscensionLevel) || 0))
  if (effective <= 0) return 0
  const rampEnd = Math.max(2, Math.floor(Number(ASCENSION_NG_TOWER_HP_FLOOR_RAMP_AT) || 4))
  if (effective >= rampEnd) return ASCENSION_NG_TOWER_HP_FLOOR
  const progress = (effective - 1) / (rampEnd - 1)
  return (
    ASCENSION_NG_TOWER_HP_FLOOR_EARLY +
    progress * (ASCENSION_NG_TOWER_HP_FLOOR - ASCENSION_NG_TOWER_HP_FLOOR_EARLY)
  )
}

/** Mult. PV boss tour (zones 1–19). */
export function getBattleTowerBossHpMult(
  ascensionLevel: number,
  enemyMitigationRanks: number = 0,
): number {
  const effective = Math.min(
    BATTLE_TOWER_ASCENSION_POWER_CAP,
    getAscensionEffectivePowerLevel(ascensionLevel),
  )
  const mitigation = Math.max(0, Math.floor(Number(enemyMitigationRanks) || 0))
  const floor = getBattleTowerNgHpFloor(effective)
  return roundAscensionMultiplier(
    Math.max(
      0.93,
      1 +
        floor +
        effective * BATTLE_TOWER_ASCENSION_HP_STEP -
        mitigation * ASCENSION_ENEMY_MITIGATION_STEP,
    ),
  )
}

/** Malus DPS NG+ en combat. `inBattleTower` applique la fraction tour. */
export function getAscensionCombatPressureMult(
  ascensionLevel: number,
  inBattleTower: boolean = false,
): number {
  const effective = getAscensionEffectivePowerLevel(ascensionLevel)
  if (effective <= 0) return 1
  const perLevel = inBattleTower
    ? ASCENSION_COMBAT_PRESSURE_PER_LEVEL * ASCENSION_TOWER_COMBAT_PRESSURE_RATIO
    : ASCENSION_COMBAT_PRESSURE_PER_LEVEL
  return roundAscensionMultiplier(1 / (1 + effective * perLevel))
}

export function scaleBattleTowerTrialBossHp(
  baseHp: number,
  ascensionLevel: number,
  enemyMitigationRanks: number = 0,
): number {
  const mult = getBattleTowerBossHpMult(ascensionLevel, enemyMitigationRanks)
  return Math.max(1, Math.round(Math.max(0, baseHp) * mult))
}

/** DPS moyen requis pour vaincre un boss tour dans le temps imparti. */
export function getBattleTowerBossRequiredDps(
  baseHp: number,
  timerSeconds: number,
  ascensionLevel: number,
  enemyMitigationRanks: number = 0,
): number {
  const hp = scaleBattleTowerTrialBossHp(baseHp, ascensionLevel, enemyMitigationRanks)
  return Math.ceil(hp / Math.max(1, Math.floor(Number(timerSeconds) || 30)))
}
